import { navStateStore, NavStateType } from "../state/nav-state-store";
import windowService from "./window-service";
import { FeatureMap } from "../state/feature-toggles-state";
import {
  MicroAgentWrapper,
  NavigationAgent,
  NavigationPageAction,
} from "./micro-agent-wrapper";
import {
  enqueueError,
  eventifyErrorQueue,
  getErrorQueue,
} from "./error-queue.service";
import {
  eventifyEventQueue,
  getEventQueue,
  NREventQueueEvent,
} from "./event-queue.service";

export interface NavigationLoadOptions {
  state?: Partial<NavStateType>;
}

export async function initMicroAgent(): Promise<NavigationAgent | null> {
  return MicroAgentWrapper.init();
}

/**
 * Error handler to enqueue errors
 * Caution: Stencil will not catch errors thrown in async code
 * To capture those, wrap the async code in a try/catch block and use the logger service
 * @param error
 */
export function navigationAgentErrorHandler(err: Error): void {
  enqueueError(err);
}

export function trackEvent(
  action: NavigationPageAction,
  options?: NavigationLoadOptions,
): void {
  MicroAgentWrapper.addPageAction(action, {
    navigationVersion: windowService.version,
    ...options,
  });
}

export function trackNavigationLoad(options?: NavigationLoadOptions): void {
  MicroAgentWrapper.addPageAction(NavigationPageAction.NAVIGATION_LOADED, {
    navigationVersion: windowService.version,
    ...options,
  });
}

export function trackNavigationDrawerLoad(): void {
  MicroAgentWrapper.addPageAction(
    NavigationPageAction.NAVIGATION_DRAWER_LOADED,
    {
      navigationVersion: windowService.version,
      drawerVersion: windowService.drawerVersion,
    },
  );
}

export function trackSeriesImagePreLoad(): void {
  MicroAgentWrapper.addPageAction(
    NavigationPageAction.NAVIGATION_SERIES_IMG_PRELOAD,
    {
      navigationVersion: windowService.version,
      drawerVersion: windowService.drawerVersion,
    },
  );
}

export function trackSeriesImagePreLoadError(error: Error): void {
  MicroAgentWrapper.addPageAction(
    NavigationPageAction.NAVIGATION_SERIES_IMG_PRELOAD_ERROR,
    {
      navigationVersion: windowService.version,
      drawerVersion: windowService.drawerVersion,
      error,
    },
  );
}

function processQueue<T>(queue: T[], processFn: (item: T) => void): void {
  while (queue.length > 0) {
    const item = queue.shift();
    if (item) {
      processFn(item);
    }
  }
}

export function processEventQueue(): void {
  const eventQueue = getEventQueue();
  if (eventQueue) {
    processQueue<NREventQueueEvent>(eventQueue, (item: NREventQueueEvent) => {
      // leave the option type as any for flexibility
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      trackEvent(item.type, item.options);
    });
  }
}

export function processErrorQueue(): void {
  const errorQueue = getErrorQueue();
  if (errorQueue) {
    processQueue<Error>(errorQueue, (item: Error) =>
      captureNaviException(item),
    );
  }
}

export function eventifyErrorHandling(): void {
  eventifyErrorQueue(processErrorQueue);
}

export function eventifyEventHandling(): void {
  eventifyEventQueue(processEventQueue);
}

export function trackFeatureToggles(features: FeatureMap): void {
  MicroAgentWrapper.addPageAction(NavigationPageAction.NAVIGATION_FEATURES, {
    features,
  });
}

function captureNaviException(err: Error): void {
  MicroAgentWrapper.noticeError(err, {
    tags: {
      navigationVersion: windowService.version || null,
      drawerVersion: windowService.drawerVersion || null,
      env: navStateStore.state.env || null,
    },
  });
}
